<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import { ref, type Ref, onMounted, getCurrentInstance } from 'vue'

const props = withDefaults(defineProps<{
  title: string
  collapseOnClickOutside?: boolean
  collapsedOnOpen?: boolean,
  ignoreClickOutsideRefs?: Ref[]
  ignoreClickOutsideUiActions?: string | string[],
}>(), { collapseOnClickOutside: true, collapsedOnOpen: false })

const emit = defineEmits<{
  (e: 'collapse', state: boolean): void
  (e: 'close'): void
}>()

const panelRef = ref(null)
const panelCollapsed = ref(props.collapsedOnOpen)

const togglePanelCollapsing = () => {
  panelCollapsed.value = !panelCollapsed.value
  emit('collapse', panelCollapsed.value)
}

const closePanel = () => {
  emit('close')
}

onClickOutside(panelRef, (ev) => {
  let ignoreEventByGivenUiAction = false

  if (ev.target && props.ignoreClickOutsideUiActions) {
    const uiActionEl = (ev.target as HTMLElement).closest('[data-ui-action]')
    const uiActions = Array.isArray(props.ignoreClickOutsideUiActions) ? props.ignoreClickOutsideUiActions : [props.ignoreClickOutsideUiActions]

    if (uiActionEl && uiActions.some((action) => action === (uiActionEl as HTMLElement).dataset.uiAction)) {
      ignoreEventByGivenUiAction = true
    }
  }
  if (!props.collapseOnClickOutside || panelCollapsed.value || ignoreEventByGivenUiAction) return
  togglePanelCollapsing()
}, { ignore: props.ignoreClickOutsideRefs || [] })

onMounted(() => {
  const instance = getCurrentInstance()

  if (instance && instance.proxy) {
    (instance.proxy as unknown as Record<string, unknown>).panelCollapsed = panelCollapsed as Ref<boolean>
    (instance.proxy as unknown as Record<string, unknown>).togglePanelCollapsing = togglePanelCollapsing
  }
})
</script>

<template>
  <div
    ref="panelRef"
    class="floating-widget fixed bottom-4 right-4 z-50 flex w-[25rem] flex-col overflow-hidden rounded-2xl bg-white"
  >
    <div class="flex items-center border-b border-neutral-300 p-4">
      <p class="t3 -t_semi-bold -pr_2 -mb_0 truncate">
        {{ title }}
      </p>
      <div class="ml-auto shrink-0">
        <sword-button-wuk
          :icon="panelCollapsed ? 'chevron-up' : 'chevron-down'"
          theme="transparent"
          size="medium"
          round
          @click.native="togglePanelCollapsing"
        />
        <sword-button-wuk
          icon="close"
          theme="transparent"
          size="medium"
          round
          @click.native="closePanel"
        />
      </div>
    </div>

    <transition name="collapse" appear>
      <div
        v-if="!panelCollapsed"
        class="floating-widget__content-main flex grow flex-col overflow-hidden"
      >
        <div class="grow overflow-y-auto overflow-x-hidden px-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <slot></slot>
        </div>

        <div v-if="$slots.footer" class="shrink-0 bg-white px-4 py-6">
          <slot name="footer"></slot>
        </div>
      </div>
    </transition>
  </div>
</template>

<style scoped lang="scss">
$widget-content-height: 75vh;

.floating-widget {
  box-shadow: 0 6px 18px 0 rgba(31, 34, 44, 0.25);
}

.floating-widget__content-main {
  transform-origin: bottom;
  height: $widget-content-height;
}

.collapse-enter-active {
  overflow: hidden;
  animation: collapse reverse 250ms ease;
}
.collapse-leave-active {
  overflow: hidden;
  animation: collapse 250ms ease;
}

@keyframes collapse {
  from {
    height: $widget-content-height;
  }
  to {
    height: 0;
  }
}
</style>
